import React, { useState, useCallback, useMemo } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, TextField, InputAdornment, Chip, Avatar, IconButton,
  Tooltip, Typography, Switch, Skeleton, Paper, MenuItem, Select,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Business as BusinessIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
}

function RoleChip({ role }) {
  const color = role?.toLowerCase().includes('super') ? 'error'
              : role?.toLowerCase().includes('admin') ? 'warning'
              : 'default';
  return role ? <Chip label={role} size="small" color={color} sx={{ fontWeight: 700, fontSize: '0.68rem' }} /> : null;
}

function StatusChip({ active }) {
  return (
    <Chip
      label={active ? 'Active' : 'Disabled'}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.68rem',
        bgcolor: active ? 'rgba(76,175,80,0.12)' : 'rgba(158,158,158,0.12)',
        color: active ? '#2e7d32' : '#757575',
      }}
    />
  );
}

// ─── Delete confirm dialog ────────────────────────────────────────────────────
function DeleteConfirmDialog({ open, user, onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: '#f44336' }}>Delete User</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to permanently delete{' '}
          <strong>{user?.Name || user?.Email}</strong>? This action cannot be undone.
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm} variant="contained" disabled={loading}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 1.5, bgcolor: '#f44336', '&:hover': { bgcolor: '#c62828' } }}
        >
          {loading ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * UserListTable
 * Props:
 *   groupedUsers: Map<company, user[]>
 *   loading: boolean
 *   onEdit: (user) => void
 *   onPassword: (user) => void
 *   onDelete: (user) => void
 *   onToggleEnable: (user, newEnabled) => void
 *   onRefresh: () => void
 */
const UserListTable = ({ groupedUsers = new Map(), loading = false, onEdit, onPassword, onDelete, onToggleEnable }) => {
  const [search, setSearch]           = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [collapsed, setCollapsed]     = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [togglingId, setTogglingId]   = useState(null);

  const companies = useMemo(() => ['', ...groupedUsers.keys()], [groupedUsers]);

  // Flatten + filter for pagination
  const flatRows = useMemo(() => {
    const rows = [];
    groupedUsers.forEach((users, company) => {
      if (companyFilter && company !== companyFilter) return;
      users.forEach((u) => rows.push({ ...u, _company: company }));
    });
    const q = search.toLowerCase();
    return q
      ? rows.filter((u) =>
          (u.Name || '').toLowerCase().includes(q) ||
          (u.Email || '').toLowerCase().includes(q) ||
          (u._company || '').toLowerCase().includes(q)
        )
      : rows;
  }, [groupedUsers, search, companyFilter]);

  const paginated = useMemo(() =>
    flatRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [flatRows, page, rowsPerPage]
  );

  const handleToggleCollapse = useCallback((company) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(company) ? next.delete(company) : next.add(company);
      return next;
    });
  }, []);

  const handleToggleEnable = useCallback(async (user) => {
    setTogglingId(user.Id || user.id);
    try {
      const isActive = user.IsActive ?? user.isActive ?? user.Enabled ?? true;
      await onToggleEnable?.(user, !isActive);
    } finally {
      setTogglingId(null);
    }
  }, [onToggleEnable]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await onDelete?.(deleteTarget);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, onDelete]);

  // Group paginated rows back by company for display
  const paginatedGrouped = useMemo(() => {
    const map = new Map();
    paginated.forEach((u) => {
      if (!map.has(u._company)) map.set(u._company, []);
      map.get(u._company).push(u);
    });
    return map;
  }, [paginated]);

  if (loading) {
    return (
      <Box>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 0.5, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Search + filter bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search users…" value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ flex: 1, minWidth: 240, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Company</InputLabel>
          <Select
            value={companyFilter} label="Filter by Company"
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(0); }}
            sx={{ borderRadius: 2 }}
            startAdornment={<FilterIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5, mr: -0.5 }} />}
          >
            <MenuItem value=""><em>All Companies</em></MenuItem>
            {[...groupedUsers.keys()].map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {flatRows.length} user{flatRows.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {flatRows.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {search || companyFilter ? 'No users match your search.' : 'No users found.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Enabled</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...paginatedGrouped.entries()].map(([company, users]) => (
                <React.Fragment key={company}>
                  {/* Company header row */}
                  <TableRow
                    sx={{ bgcolor: '#ede7f6', cursor: 'pointer' }}
                    onClick={() => handleToggleCollapse(company)}
                  >
                    <TableCell colSpan={7} sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 15, color: '#7e57c2' }} />
                        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#5e35b1', flex: 1 }}>
                          {company}
                        </Typography>
                        <Chip label={users.length} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#d1c4e9', color: '#4527a0' }} />
                        {collapsed.has(company) ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#7e57c2' }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: '#7e57c2' }} />}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {!collapsed.has(company) && users.map((user) => {
                    const userId   = user.Id || user.id;
                    const isActive = user.IsActive ?? user.isActive ?? user.Enabled ?? true;
                    const toggling = togglingId === userId;
                    return (
                      <TableRow key={userId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        {/* User */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: '0.72rem', bgcolor: '#667eea' }}>
                              {getInitials(user.Name)}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                              {user.Name || '—'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Email */}
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {user.Email}
                        </TableCell>

                        {/* Company */}
                        <TableCell sx={{ fontSize: '0.78rem' }}>{company}</TableCell>

                        {/* Role */}
                        <TableCell><RoleChip role={user.Role || user.role || user.RoleName} /></TableCell>

                        {/* Status chip */}
                        <TableCell align="center"><StatusChip active={isActive} /></TableCell>

                        {/* Enable/disable toggle */}
                        <TableCell align="center">
                          <Tooltip title={isActive ? 'Disable user' : 'Enable user'}>
                            <span>
                              <Switch
                                size="small"
                                checked={isActive}
                                disabled={toggling}
                                onChange={() => handleToggleEnable(user)}
                                color="success"
                              />
                            </span>
                          </Tooltip>
                        </TableCell>

                        {/* Action buttons */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                            <Tooltip title="Edit user">
                              <IconButton size="small" onClick={() => onEdit?.(user)} sx={{ color: '#667eea' }}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset password">
                              <IconButton size="small" onClick={() => onPassword?.(user)} sx={{ color: '#ff9800' }}>
                                <LockIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user">
                              <IconButton size="small" onClick={() => setDeleteTarget(user)} sx={{ color: '#f44336' }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={flatRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={!!deleteTarget}
        user={deleteTarget}
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </Box>
  );
};

export default React.memo(UserListTable);
